"use client";

import { useState } from "react";
import type { AdvisorCTAContent, DomainConfig } from "../types";
import AppointmentBlock from "./AppointmentBlock";
import CtaModal from "./CtaModal";

export type AdvisorCTAVariant =
  | "horizontal"    // Variant 1: zacht horizontaal (foto links, tekst rechts) — default
  | "card"          // Variant 2: centrale card met schaduw
  | "dark"          // Variant 3: donkere achtergrond, filmische italic quote
  | "split-hours"   // Variant 4: adviseur-intro links, CTA+telefoon+openingstijden rechts
  | "note";         // Variant 5: foto + italic naam links, "briefje" speech-bubble rechts

interface Props {
  content: AdvisorCTAContent;
  /** Public URL voor de adviseur-portret. Fallback: decoratieve SVG. */
  image_src?: string;
  image_focal_x?: number;
  image_focal_y?: number;
  phone?: string;
  variant?: AdvisorCTAVariant;
  /** Wordt door applyCityToBlock geïnjecteerd — voor stadspecifieke alt-texts */
  city?: string;
  /** "modal" (default): CTA opent popup met AppointmentBlock variant=advies-afspraak.
   *  "anchor": gewone link (bv. naar #brochure-form). */
  cta_mode?: "modal" | "anchor";
  /** Vereist voor cta_mode='modal' — AppointmentBlock heeft domain nodig
   *  voor lead-creation (brochure_page_id) + tracking. */
  domain?: DomainConfig;
  /** Niche voor AppointmentBlock tracking-context. */
  niche?: string;
}

/**
 * "Spreek met een adviseur" blok met 5 layout-varianten.
 *
 * Port van Claude Design "Adviseur Blok.html":
 *   - horizontal  : rustig, horizontaal, foto links
 *   - card        : centrale witte card met schaduw
 *   - dark        : donker, grote italic quote
 *   - split-hours : adviseur + CTA/telefoon/openingstijden
 *   - note        : briefje speech-bubble met handtekening-naam
 *
 * Alle varianten lezen advisor-specifieke velden uit `content` (optioneel):
 * `advisor_name`, `advisor_role`, `chip_text`, `eyebrow`, `quote`, `hours`.
 * Fallbacks zijn uit het ontwerp ("Marieke" / "Senior adviseur" etc).
 */
export default function AdvisorCTA({
  content,
  image_src,
  image_focal_x = 50,
  image_focal_y = 50,
  phone,
  variant = "horizontal",
  city,
  cta_mode = "modal",
  domain,
  niche,
}: Props) {
  const shared = { content, image_src, image_focal_x, image_focal_y, phone, city };
  const [modalOpen, setModalOpen] = useState(false);

  // Wanneer cta_mode='modal' EN domain gegeven: vang clicks op de CTA-link
  // op (bubbling) en open modal ipv navigeren. Voor andere modes werkt de
  // <a href={cta_href}> als gewone anchor-link.
  const ctaHref = (content as { cta_href?: string }).cta_href ?? "#brochure-form";
  const useModal = cta_mode === "modal" && !!domain;
  const onWrapperClick = (e: React.MouseEvent) => {
    if (!useModal) return;
    const target = (e.target as HTMLElement).closest("a") as HTMLAnchorElement | null;
    if (!target) return;
    if (target.getAttribute("href") !== ctaHref) return;
    e.preventDefault();
    setModalOpen(true);
  };

  let variantNode: React.ReactNode;
  switch (variant) {
    case "card":
      variantNode = <CardVariant {...shared} />;
      break;
    case "dark":
      variantNode = <DarkVariant {...shared} />;
      break;
    case "split-hours":
      variantNode = <SplitHoursVariant {...shared} />;
      break;
    case "note":
      variantNode = <NoteVariant {...shared} />;
      break;
    case "horizontal":
    default:
      variantNode = <HorizontalVariant {...shared} />;
      break;
  }

  return (
    <>
      <div onClick={onWrapperClick}>{variantNode}</div>
      {useModal && domain ? (
        <CtaModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <AppointmentBlock
            content={{}}
            variant="advies-afspraak"
            domain={domain}
            {...(niche ? { niche } : {})}
            embedded
          />
        </CtaModal>
      ) : null}
    </>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────────
interface SharedProps {
  content: AdvisorCTAContent;
  image_src: string | undefined;
  image_focal_x: number | undefined;
  image_focal_y: number | undefined;
  phone: string | undefined;
  city: string | undefined;
}

interface ExtendedContent extends AdvisorCTAContent {
  advisor_name?: string;
  advisor_role?: string;
  chip_text?: string;
  eyebrow?: string;
  quote?: string;
  hours?: string;
}

function readExtra(content: AdvisorCTAContent) {
  const c = content as ExtendedContent;
  return {
    name: c.advisor_name ?? "Marieke",
    role: c.advisor_role ?? "Senior adviseur",
    chip: c.chip_text ?? "Vraag een adviesgesprek",
    eyebrow: c.eyebrow,
    quote: c.quote,
    hours: c.hours,
  };
}

function Portrait({
  image_src,
  image_focal_x = 50,
  image_focal_y = 50,
  size = "lg",
  tone = "light",
  name,
  city,
}: {
  image_src: string | undefined;
  image_focal_x: number | undefined;
  image_focal_y: number | undefined;
  size?: "sm" | "md" | "lg" | "xl";
  tone?: "light" | "dark";
  name?: string;
  city?: string;
}) {
  const sizeClass =
    size === "sm"
      ? "h-24 w-24"
      : size === "md"
      ? "h-36 w-36 md:h-40 md:w-40"
      : size === "xl"
      ? "h-48 w-48 md:h-64 md:w-64"
      : "h-40 w-40 md:h-56 md:w-56";
  if (image_src) {
    // Generieke fallback — vermijdt hardcoded brand. Bij multi-site (BW
    // vs Zwaluw) heeft een specifiek brand-fallback geen meerwaarde voor
    // alt-text — zoekbots herkennen "Onze badkameradviseur" prima.
    const who = name ? `Adviseur ${name}` : "Onze badkameradviseur";
    const alt = city ? `${who} — badkamerrenovatie ${city}` : who;
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={image_src}
        alt={alt}
        className={`${sizeClass} rounded-full object-cover shadow-card-hover ring-4 ring-white`}
        style={{ objectPosition: `${image_focal_x}% ${image_focal_y}%` }}
        loading="lazy"
      />
    );
  }
  const gradient =
    tone === "dark"
      ? "bg-[radial-gradient(circle_at_35%_35%,#1E3A5F_0%,#0F1F33_100%)]"
      : "bg-[radial-gradient(circle_at_35%_35%,var(--color-secondary-light,#D3DDE8)_0%,var(--color-secondary,#1E3A5F)_100%)]";
  const strokeColor = tone === "dark" ? "#F1EEE7" : "#2C2E2B";
  return (
    <div className={`${sizeClass} rounded-full ${gradient} grid place-items-center ring-2 ${tone === "dark" ? "ring-white/20" : "ring-white"} shadow-card-hover`}>
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        className="h-2/5 w-2/5 opacity-55"
        fill="none"
        stroke={strokeColor}
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <circle cx="50" cy="36" r="14" />
        <path d="M22 80 C22 62, 38 56, 50 56 C62 56, 78 62, 78 80" />
      </svg>
    </div>
  );
}

function CtaButton({ content, compact }: { content: AdvisorCTAContent; compact?: boolean }) {
  return (
    <a
      href={content.cta_href ?? "#brochure-form"}
      style={{ background: "var(--terracotta, #FF6B1A)" }}
      className={`inline-flex items-center justify-center gap-2 rounded-pill text-white font-semibold shadow-card hover:brightness-95 hover:shadow-card-hover transition-all focus:outline-none focus-visible:shadow-focus ${compact ? "px-5 py-2.5 text-sm" : "px-6 py-3"}`}
    >
      {content.cta_text ?? "Vraag adviesgesprek aan"}
      <span aria-hidden>→</span>
    </a>
  );
}

function PhoneLink({ phone, tone = "light" }: { phone: string | undefined; tone?: "light" | "dark" }) {
  if (!phone) return null;
  const color = tone === "dark" ? "text-cream-100/85" : "text-ink";
  const hoverColor = tone === "dark" ? "hover:text-cream" : "hover:text-terracotta";
  return (
    <a
      href={`tel:${phone.replace(/\s/g, "")}`}
      className={`inline-flex items-center gap-2 ${color} ${hoverColor} font-semibold transition-colors`}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
      Of bel {phone}
    </a>
  );
}

function Chip({ text }: { text: string }) {
  return (
    <span
      style={{ color: "var(--sage, #1E3A5F)" }}
      className="inline-flex items-center gap-2 rounded-pill bg-white px-3 py-1 text-sm font-medium"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      {text}
    </span>
  );
}

// ── Variant 1: horizontaal zacht ───────────────────────────────────────────
function HorizontalVariant({ content, image_src, image_focal_x, image_focal_y, phone, city }: SharedProps) {
  const xt = readExtra(content);
  return (
    <section className="py-14 md:py-20 bg-cream-100 border-y border-stone-100">
      <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-[auto_1fr] items-center gap-8 md:gap-14">
        <div className="flex justify-center">
          <Portrait image_src={image_src} image_focal_x={image_focal_x} image_focal_y={image_focal_y} size="lg" {...(xt.name ? { name: xt.name } : {})} {...(city ? { city } : {})} />
        </div>
        <div className="text-center md:text-left">
          <div className="mb-4">
            <Chip text={xt.chip} />
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight tracking-tight">
            {content.headline ?? "Liever eerst even sparren?"}
          </h2>
          <p className="mt-3 text-lg text-stone-700 leading-relaxed max-w-xl md:mx-0 mx-auto">
            {content.body ??
              `Onze senior-adviseur ${xt.name} neemt rustig de tijd om uw situatie en wensen door te nemen — zonder verplichtingen. De brochure is een prima vertrekpunt, maar bellen mag ook altijd.`}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center md:justify-start gap-4">
            <CtaButton content={content} />
            <PhoneLink phone={phone} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 2: centrale card ───────────────────────────────────────────────
function CardVariant({ content, image_src, image_focal_x, image_focal_y, phone, city }: SharedProps) {
  const xt = readExtra(content);
  return (
    <section className="py-14 md:py-20 bg-cream-100">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-card border border-stone-100 shadow-card-hover px-7 py-10 md:px-14 md:py-12 grid md:grid-cols-[auto_1fr] items-center gap-6 md:gap-10 text-center md:text-left">
          <div className="flex justify-center md:justify-self-start">
            <Portrait image_src={image_src} image_focal_x={image_focal_x} image_focal_y={image_focal_y} size="md" {...(xt.name ? { name: xt.name } : {})} {...(city ? { city } : {})} />
          </div>
          <div>
            <div style={{ color: "var(--terracotta, #FF6B1A)" }} className="text-[11px] font-bold tracking-[0.18em] uppercase mb-2">
              {xt.eyebrow ?? "Persoonlijk advies"}
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-ink leading-snug">
              {content.headline ?? `${xt.name} helpt u graag verder`}
            </h2>
            <p className="mt-3 text-base text-stone-700 leading-relaxed">
              {content.body ?? `${xt.role} met ruim 15 jaar ervaring. Kies wat voor u prettig is — een gesprek op kantoor, telefonisch, of gewoon de brochure.`}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center md:justify-start gap-4">
              <CtaButton content={content} compact />
              <PhoneLink phone={phone} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: dark, filmisch ──────────────────────────────────────────────
function DarkVariant({ content, image_src, image_focal_x, image_focal_y, phone, city }: SharedProps) {
  const xt = readExtra(content);
  const quote =
    xt.quote ??
    '"Een nieuwe badkamer is een grote stap — laten we er samen rustig over nadenken."';
  return (
    <section className="py-16 md:py-24 bg-ink text-cream-100">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 items-center gap-10 md:gap-16 text-center md:text-left">
        <div className="flex md:justify-end">
          <Portrait image_src={image_src} image_focal_x={image_focal_x} image_focal_y={image_focal_y} size="xl" tone="dark" {...(xt.name ? { name: xt.name } : {})} {...(city ? { city } : {})} />
        </div>
        <div>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase mb-4" style={{ color: "#E8A48B" }}>
            — {xt.name}, {xt.role}
          </div>
          <h2 className="font-display italic text-3xl md:text-4xl leading-snug tracking-tight mb-4">
            {quote}
          </h2>
          <p className="text-base md:text-lg text-cream-100/70 leading-relaxed mb-6 max-w-md md:mx-0 mx-auto">
            {content.body ?? "Vrijblijvend gesprek, zonder druk. Op afspraak in onze showroom of gewoon telefonisch — wat u prettig vindt."}
          </p>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-5">
            <CtaButton content={content} />
            <PhoneLink phone={phone} tone="dark" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 4: split met openingstijden ───────────────────────────────────
function SplitHoursVariant({ content, image_src, image_focal_x, image_focal_y, phone, city }: SharedProps) {
  const xt = readExtra(content);
  return (
    <section className="py-12 md:py-16 bg-cream-100 border-y border-stone-100">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1.4fr_1fr] items-center gap-8 md:gap-14">
        <div className="grid grid-cols-[auto_1fr] items-center gap-5 md:gap-7">
          <Portrait image_src={image_src} image_focal_x={image_focal_x} image_focal_y={image_focal_y} size="sm" {...(xt.name ? { name: xt.name } : {})} {...(city ? { city } : {})} />
          <div>
            <div className="mb-2.5">
              <Chip text={xt.chip} />
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-ink leading-snug">
              {content.headline ?? "Liever eerst even sparren?"}
            </h2>
            <p className="mt-2 text-sm md:text-base text-stone-700 leading-relaxed">
              {content.body ?? `${xt.name}, onze ${xt.role.toLowerCase()}, denkt vrijblijvend met u mee — zonder verkooppraatjes.`}
            </p>
          </div>
        </div>
        <div className="bg-white border border-stone-100 rounded-card px-6 py-6 md:px-7 md:py-7 space-y-4">
          <CtaButton content={content} />
          <div className="h-px bg-stone-200" />
          {phone ? (
            <div>
              <div className="text-[11px] font-bold tracking-[0.14em] text-stone-600 uppercase mb-1.5">
                Of bel direct
              </div>
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="font-display text-xl md:text-2xl text-ink inline-flex items-center gap-2.5"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                {phone}
              </a>
              {xt.hours ? (
                <div className="text-sm text-stone-700 mt-1.5 leading-relaxed">{xt.hours}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Variant 5: briefje / speech-bubble ────────────────────────────────────
function NoteVariant({ content, image_src, image_focal_x, image_focal_y, phone, city }: SharedProps) {
  const xt = readExtra(content);
  const note =
    content.body ??
    '"Een badkamer kies je niet elke dag. Bel me gerust, dan denken we eerst rustig met u mee — pas als het klopt, gaan we verder."';
  return (
    <section className="py-14 md:py-20 bg-cream-100 border-y border-stone-100">
      <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-[260px_1fr] items-start gap-8 md:gap-12">
        <div className="flex flex-col items-center text-center">
          <Portrait image_src={image_src} image_focal_x={image_focal_x} image_focal_y={image_focal_y} size="lg" {...(xt.name ? { name: xt.name } : {})} {...(city ? { city } : {})} />
          <div className="font-display italic text-2xl md:text-3xl text-ink mt-3.5">{xt.name}</div>
          <div className="text-xs tracking-[0.14em] text-stone-600 uppercase mt-0.5 font-semibold">
            {xt.role}
          </div>
        </div>
        <div className="relative bg-white border border-stone-100 rounded-card shadow-card-hover px-6 py-6 md:px-9 md:py-8">
          <div
            aria-hidden
            className="hidden md:block absolute -left-[11px] top-12 w-[22px] h-[22px] bg-white border-l border-b border-stone-100 rotate-45"
          />
          <div style={{ color: "var(--terracotta, #FF6B1A)" }} className="text-[11px] font-bold tracking-[0.16em] uppercase mb-3.5 inline-flex items-center gap-2">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Briefje van {xt.name}
          </div>
          <p className="font-display text-lg md:text-2xl text-ink leading-snug mb-4">{note}</p>
          <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-dashed border-stone-200">
            <CtaButton content={content} compact />
            {phone ? (
              <div className="flex items-center gap-2 text-sm text-stone-700">
                <span>of bel direct</span>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="text-ink font-semibold border-b border-ink pb-px"
                >
                  {phone}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
